import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../../components/Navbar';
import ExportButtons from '../../components/ExportButtons';
import { Link } from 'react-router-dom';
import { Calendar, Users, Coffee, Sunrise, Sun, Moon, Loader2, ClipboardList } from 'lucide-react';
import { VegIndicator } from '../../components/VegIndicator';
import logger from '../../lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MEAL_META = {
  breakfast: { icon: Sunrise, label: 'Breakfast', emoji: '🌅' },
  lunch:     { icon: Sun, label: 'Lunch', emoji: '🍽️' },
  snacks:    { icon: Coffee, label: 'Snacks', emoji: '☕' },
  dinner:    { icon: Moon, label: 'Dinner', emoji: '🌙' },
};

const VendorReservations = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/reservations/vendor/counts`, { withCredentials: true });
        setData(data);
      } catch (e) {
        logger.error(e);
      } finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handleExport = () => {
    if (!data?.reservations?.length) return;
    const csv = [
      ['Meal', 'Meal Type', 'Amount', 'Payment', 'Source', 'Employee Name', 'Email', 'Pickup QR', 'Reserved At'].join(','),
      ...data.reservations.map(r => [
        r.meal_period,
        `"${(r.meal_type_label || r.meal_type || '').replace(/"/g, "'")}"`,
        r.total_amount ?? '',
        r.payment_status || '',
        r.source || 'employee',
        r.employee_name,
        r.employee_email || '',
        r.pickup_qr,
        r.created_at,
      ].join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cravitoo-reservations-${data.date}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="mb-6 sm:mb-8 flex flex-wrap justify-between items-start gap-3">
            <div className="min-w-0 w-full sm:w-auto sm:flex-1">
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight sm:tracking-tighter font-semibold text-text-primary break-words">
                Tomorrow's Reservations
              </h1>
              <p className="text-text-secondary mt-2 flex items-center gap-2 text-sm sm:text-base">
                <Calendar className="h-4 w-4" /> {data?.date}
              </p>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
            <Link to="/vendor/preorder-menu" data-testid="manage-preorder-menu-link"
              className="flex-1 sm:flex-none justify-center bg-card px-4 py-2.5 rounded-lg border border-border-light text-sm font-medium flex items-center gap-1.5 hover:border-primary">
              <ClipboardList className="h-4 w-4" /> Pre-order Menu
            </Link>
            <button
              onClick={handleExport}
              disabled={!data?.reservations?.length}
              data-testid="export-csv-btn"
              className="flex-1 sm:flex-none bg-primary hover:bg-primary-hover disabled:opacity-40 text-white px-4 sm:px-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
            >
              Export CSV
            </button>
            </div>
          </div>
          {data?.preorder_mode === 'custom' && (
            <p data-testid="vendor-custom-revenue" className="mb-4 text-sm text-text-secondary">
              Using your own pre-order menu · Paid online for tomorrow: <strong className="text-text-primary">₹{Number(data.custom_paid_revenue || 0).toFixed(0)}</strong>
            </p>
          )}

          <div className="mb-5 sm:mb-6 flex flex-wrap items-center justify-end gap-2 sm:gap-3">
            <span className="text-xs text-text-muted">Sales reports (last 30 days):</span>
            <ExportButtons endpoint="/exports/vendor-sales" filename="cravitoo-vendor-sales" testidPrefix="vendor-sales" />
          </div>

          {/* Head-count cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5 sm:mb-6">
            {Object.entries(MEAL_META).map(([meal, meta]) => {
              const c = data?.counts?.[meal] || { reserved: 0, consumed: 0 };
              const Icon = meta.icon;
              const byType = data?.by_meal_type?.[meal] || {};
              const labels = data?.meal_type_labels || {};
              return (
                <div key={meal} data-testid={`count-${meal}`} className="bg-card border border-border-light rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="bg-primary-light rounded-lg p-2">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    <p className="text-sm font-medium text-text-secondary">{meta.emoji} {meta.label}</p>
                  </div>
                  <p className="font-heading text-3xl font-semibold text-text-primary">{c.reserved}</p>
                  <p className="text-xs text-text-muted mt-1">{c.consumed} consumed</p>
                  {Object.values(byType).some(n => n > 0) && (
                    <div className="mt-3 pt-3 border-t border-border-light space-y-1" data-testid={`meal-type-breakdown-${meal}`}>
                      {Object.entries(byType).map(([mt, n]) => n > 0 ? (
                        <div key={mt} className="flex justify-between text-xs">
                          <span className="text-text-muted">{labels[mt] || mt}</span>
                          <span className="font-mono text-text-primary font-semibold">{n}</span>
                        </div>
                      ) : null)}
                    </div>
                  )}
                  {data?.by_item?.[meal]?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border-light space-y-1" data-testid={`custom-item-breakdown-${meal}`}>
                      {data.by_item[meal].map((it) => (
                        <div key={it.item_id} className="flex justify-between gap-2 text-xs">
                          <span className="text-text-muted flex items-start gap-1.5 min-w-0"><VegIndicator isVeg={it.is_veg} size="sm" className="mt-0.5" /> <span className="break-words">{it.name}</span></span>
                          <span className="font-mono text-text-primary font-semibold">{it.quantity}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Grand total */}
          <div className="bg-gradient-to-r from-primary to-orange-600 text-white rounded-2xl p-5 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8" />
              <div>
                <p className="text-sm opacity-90">Total reservations</p>
                <p className="font-heading text-3xl font-semibold">{data?.total || 0}</p>
              </div>
            </div>
            <p className="text-sm opacity-90">Plan prep accordingly</p>
          </div>

          {/* Detailed list */}
          <div className="bg-card border border-border-light rounded-2xl overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-border-light">
              <h2 className="font-heading text-lg sm:text-xl font-semibold text-text-primary">Customer List</h2>
              <p className="text-xs sm:text-sm text-text-secondary">Showing all active reservations for tomorrow</p>
            </div>
            {data?.reservations?.length === 0 ? (
              <p data-testid="empty-reservations" className="p-8 text-center text-text-muted">No reservations yet for tomorrow.</p>
            ) : (
              <div className="overflow-x-auto">
              <table className="w-full min-w-[560px]">
                <thead className="bg-background text-xs text-text-muted uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-3">Meal</th>
                    <th className="text-left px-5 py-3">Type</th>
                    <th className="text-left px-5 py-3">Employee</th>
                    <th className="text-left px-5 py-3 hidden sm:table-cell">Email</th>
                    <th className="text-left px-5 py-3 hidden md:table-cell">Reserved at</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.reservations?.map((r) => (
                    <tr key={r.id} data-testid={`vendor-reservation-${r.id}`} className="border-t border-border-light hover:bg-background/50">
                      <td className="px-5 py-3 text-sm">
                        <span className="capitalize">{MEAL_META[r.meal_period]?.emoji} {r.meal_period}</span>
                      </td>
                      <td className="px-5 py-3 text-sm">
                        <span className={`px-2 py-0.5 text-xs rounded-full inline-block break-words ${
                          r.meal_type === 'custom'
                            ? (r.is_veg ? 'bg-emerald-50 text-emerald-700'
                              : (r.items || []).some((it) => it.is_veg) ? 'bg-slate-100 text-slate-700' : 'bg-rose-50 text-rose-700')
                            : r.meal_type?.startsWith('veg') ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                        }`}>{r.meal_type_label || '—'}</span>
                        {r.total_amount != null && (
                          <span data-testid={`vendor-reservation-amount-${r.id}`} className={`ml-2 text-xs px-2 py-0.5 rounded-full ${r.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                            ₹{Number(r.total_amount).toFixed(0)} {r.payment_status === 'paid' ? 'paid' : 'at counter'}
                          </span>
                        )}
                        {r.source === 'corporate_bulk' && (
                          <span className="ml-2 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">Bulk</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-sm text-text-primary font-medium">{r.employee_name}</td>
                      <td className="px-5 py-3 text-sm text-text-secondary hidden sm:table-cell">{r.employee_email || '—'}</td>
                      <td className="px-5 py-3 text-sm text-text-muted hidden md:table-cell">{new Date(r.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default VendorReservations;
